import { Group, ActionIcon, Highlight, Stack } from "@mantine/core"
import { useInterval } from "@mantine/hooks"
import {
  IconRefresh,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react"
import { useNavigate } from "@tanstack/react-router"
import { MRT_ColumnDef } from "mantine-react-table"
import { DateTimeRangeSelector } from "../../../-components/infinit-scrolling-table/date-time-range-selector"
import {
  ExtendedInfinitScrollingTable,
  SearchProps,
} from "../../../-components/infinit-scrolling-table/extended-infinit-scrolling-table"
import { DateTime } from "../../../../../@client/System"
import { LogDto } from "../../../../../@client/Theasoft_ApiServer_Logs"
import { useInvalidate } from "../../../../../typed-client/hooks/use-invalidate"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { LogLevel, LogLevelSelect } from "../../../-components/log-level"
import { useTranslation } from "react-i18next"
import { AdminInfiniteUserSelect } from "@components/InfiniteUserSelect"
import classes from "./logging-table.module.css"
import { PersonNameWithUserNameFallback } from "@/routes/-components/chat/ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { HideableFilterGroup } from "@/routes/app/-components/hideable-filter-group"

const route = "/api/Log/GetPaginatedLogData"

interface Props {
  searchProps: SearchProps<typeof route>
  showFilters: boolean
}

export function LoggingTable({ searchProps, showFilters }: Props) {
  const { t } = useTranslation("app_server")
  const columns = React.useMemo<MRT_ColumnDef<LogDto>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "timeStamp",
        header: "",
        minSize: 125,
        size: 125,
        maxSize: 125,
        Cell: ({ cell }) =>
          getFormattedDate(cell.getValue<DateTime>(), "L LTS"),
      },
      {
        accessorKey: "level",
        header: "",
        minSize: 55,
        size: 55,
        maxSize: 55,
        Cell: ({ cell }) => <LogLevel value={cell.getValue<LogLevel>()} />,
      },
      {
        accessorKey: "userId",
        header: "",
        minSize: 120,
        maxSize: 120,
        size: 120,
        Cell: ({ cell }) => {
          const userId = cell.getValue<number | null>()
          return userId ? (
            <PersonNameWithUserNameFallback userId={userId} />
          ) : null
        },
      },
      {
        accessorKey: "message",
        header: "",
        size: 1000,
        Cell: ({ row }) => (
          <Highlight size="xs" highlight={searchProps.value.globalFilter}>
            {row.original.message}
          </Highlight>
        ),
        Filter: () => null,
      },
      { accessorKey: "exception", header: "Exception" },
    ],
    [searchProps.value]
  )
  const invalidate = useInvalidate()
  const { start, stop, active } = useInterval(() => invalidate(route), 5 * 1000)
  const navigate = useNavigate()
  return (
    <Stack h={"100&"}>
      <HideableFilterGroup visible={showFilters}>
        <DateTimeRangeSelector
          value={{
            start: searchProps.value.start,
            end: searchProps.value.end,
          }}
          onConfirm={(value) =>
            searchProps.onChange({ ...searchProps.value, ...value })
          }
          placeholder={t("TimePeriod")}
          maxDate={new Date()}
        />
        <LogLevelSelect
          value={searchProps.value.level}
          onChange={(value) =>
            searchProps.onChange({ ...searchProps.value, level: value! })
          }
        />{" "}
        <AdminInfiniteUserSelect
          styles={{ dropdown: { minWidth: 200 } }}
          placeholder="User"
          onSelectionChange={(v) =>
            searchProps.onChange({
              ...searchProps.value,
              userId: v != null ? parseInt(v) : null,
            })
          }
          value={searchProps.value.userId?.toString()}
          searchable
          clearable
          filterOptions={{ filterInactive: false, userTypeFilter: "Users" }}
        />
      </HideableFilterGroup>
      <ExtendedInfinitScrollingTable
        url={route}
        columnDefinitions={columns}
        defaultColumVisibility={{
          id: false,
          exception: false,
          logEvent: false,
        }}
        partialInput={{ ...searchProps.value }}
        searchProps={searchProps}
        defaultSorting={[{ id: "id", desc: true }]}
        forceTableState={true}
        tableState={{ showColumnFilters: true }}
        tableOptions={{
          mantineTableProps: { className: classes.testTd },
          enableSorting: false,
          enableColumnFilters: false,
          enableColumnActions: false,
          enableGlobalFilter: true,
          enableColumnResizing: false,
          enableColumnDragging: false,
          enableTableHead: false,
          renderTopToolbarCustomActions: () => (
            <Group>
              <ActionIcon
                variant="transparent"
                c={"gray"}
                onClick={() => invalidate(route)}
              >
                <IconRefresh />
              </ActionIcon>
              {active ? (
                <ActionIcon variant="transparent" c={"gray"} onClick={stop}>
                  <IconPlayerPause />
                </ActionIcon>
              ) : (
                <ActionIcon variant="transparent" c={"gray"} onClick={start}>
                  <IconPlayerPlay />
                </ActionIcon>
              )}
            </Group>
          ),
        }}
        onClickRow={(log) =>
          navigate({
            to: "/app/admin-settings/logging/$id",
            params: { id: log.id.toString() },
          })
        }
      />
    </Stack>
  )
}
